import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';

export class MediaGallery extends Component {
  #fetchController;

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#controller;
    document.addEventListener('variant:selected', this.#handleVariantSelected, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, { signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#fetchController?.abort();
  }

  #handleVariantSelected = async () => {
    const variantPicker = document.querySelector('variant-picker');
    if (!variantPicker) return;

    await new Promise((resolve) => setTimeout(resolve, 50));

    const urlParams = new URLSearchParams(window.location.search);
    let variantId = urlParams.get('variant');
    if (!variantId) {
      const checkedInput = variantPicker.querySelector('fieldset input:checked');
      variantId = checkedInput?.dataset?.variantId;
    }
    if (!variantId) return;

    const sectionId = variantPicker.dataset?.sectionId;
    if (!sectionId) return;

    const url = `${window.location.pathname}?variant=${variantId}&section_id=${sectionId}`;

    this.#fetchController?.abort();
    this.#fetchController = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#fetchController.signal });
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      const newGallery = html.querySelector('media-gallery');
      if (!newGallery) return;

      // Get all new media containers from the fetched HTML
      const newContainers = newGallery.querySelectorAll('.product-media-container');
      const currentContainers = this.querySelectorAll('.product-media-container');
      const currentThumbnails = this.querySelectorAll('.media-gallery__thumbnail');

      if (!newContainers.length) return;

      // Get the first new container's image src
      const newFirstImg = newContainers[0].querySelector('img');
      const currentFirstImg = currentContainers[0]?.querySelector('img');

      if (newFirstImg && currentFirstImg) {
        // Just swap the src and srcset of the visible image
        currentFirstImg.src = newFirstImg.src;
        currentFirstImg.srcset = newFirstImg.srcset || '';
        currentFirstImg.alt = newFirstImg.alt || '';
      }

      // Update thumbnail images too
      const newThumbnails = newGallery.querySelectorAll('.media-gallery__thumbnail img');
      currentThumbnails.forEach((thumb, i) => {
        const thumbImg = thumb.querySelector('img');
        const newThumbImg = newThumbnails[i];
        if (thumbImg && newThumbImg) {
          thumbImg.src = newThumbImg.src;
          thumbImg.alt = newThumbImg.alt || '';
        }
      });

      // Reset to first slide
      currentContainers.forEach((container, i) => {
        if (i === 0) {
          container.setAttribute('data-active', 'true');
          container.style.display = 'block';
        } else {
          container.removeAttribute('data-active');
          container.style.display = 'none';
        }
      });

      currentThumbnails.forEach((btn, i) => {
        btn.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      });

    } catch (error) {
      if (error.name !== 'AbortError') console.error('[MediaGallery] fetch error:', error);
    }
  };

  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  zoom(index, event) { this.refs.zoomDialogComponent?.open(index, event); }
  get slideshow() { return this.refs.slideshow; }
  get media() { return this.refs.media; }
  get presentation() { return this.dataset.presentation; }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
